import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateInbox,
  useAnalyzeInbox,
  useTranscribeAudio,
  getListInboxQueryKey,
  getGetTodayQueryKey,
  type InboxItem,
} from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";

export type CaptureSourceType = "text" | "voice" | "email";

export interface UseCaptureOptions {
  /**
   * Called once an inbox item has been created (before AI analysis completes).
   * Useful e.g. to close a drawer or clear local UI state.
   */
  onCaptured?: (item: InboxItem) => void;
  /**
   * Called once the AI analysis succeeds. Defaults to a "Suggestions prêtes" toast.
   * Pass a custom callback to replace the default toast (e.g. add an action button).
   */
  onAnalyzed?: (item: InboxItem) => void;
}

/**
 * Shared capture pipeline used by both the inline QuickCapture form
 * (Today dashboard + Inbox page) and the floating CaptureFab drawer.
 *
 * Pipeline:
 *  - text input  → POST /inbox → POST /inbox/:id/analyze
 *  - voice input → MediaRecorder → POST /transcribe → POST /inbox → POST /inbox/:id/analyze
 *
 * On every success step, invalidates inbox + today queries so dashboards refresh.
 */
export function useCapture(options: UseCaptureOptions = {}) {
  const [text, setText] = useState("");
  const [sourceType, setSourceType] = useState<CaptureSourceType>("text");
  const [isRecording, setIsRecording] = useState(false);
  // Optional pre-selected project: when set we prepend a "Projet : <name>"
  // hint so the AI analysis attaches generated suggestions to that project.
  const [projectId, setProjectId] = useState<string>("");
  const [projectName, setProjectName] = useState<string>("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createInbox = useCreateInbox();
  const analyzeInbox = useAnalyzeInbox();
  const transcribeAudio = useTranscribeAudio();

  const setSelectedProject = (id: string, name?: string) => {
    setProjectId(id);
    setProjectName(name ?? "");
  };

  const decorateText = (raw: string): string => {
    if (!projectName.trim()) return raw;
    const hint = `[Projet : ${projectName.trim()}]`;
    return raw.startsWith(hint) ? raw : `${hint}\n${raw}`;
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: getListInboxQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
  };

  const submitInboxItem = (
    type: CaptureSourceType,
    rawText: string,
    transcriptText?: string,
  ) => {
    const decoratedRaw = decorateText(rawText);
    const decoratedTranscript = transcriptText
      ? decorateText(transcriptText)
      : undefined;
    createInbox.mutate(
      {
        data: {
          sourceType: type,
          rawText: decoratedRaw,
          ...(decoratedTranscript ? { transcriptText: decoratedTranscript } : {}),
        },
      },
      {
        onSuccess: (item) => {
          setText("");
          queryClient.invalidateQueries({ queryKey: getListInboxQueryKey() });
          toast({ title: "Capturé. Analyse en cours…" });
          options.onCaptured?.(item);

          analyzeInbox.mutate(
            { id: item.id },
            {
              onSuccess: () => {
                invalidateAll();
                if (options.onAnalyzed) {
                  options.onAnalyzed(item);
                } else {
                  toast({ title: "Suggestions prêtes" });
                }
              },
              onError: () => {
                toast({ title: "Élément capturé, analyse à réessayer" });
                queryClient.invalidateQueries({ queryKey: getListInboxQueryKey() });
              },
            },
          );
        },
        onError: () => toast({ title: "Échec de la capture", variant: "destructive" }),
      },
    );
  };

  const submitText = () => {
    if (!text.trim()) return;
    submitInboxItem(sourceType, text);
  };

  const toggleRecording = async () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
          stream.getTracks().forEach((t) => t.stop());
          if (audioBlob.size === 0) {
            toast({ title: "Aucun audio enregistré", variant: "destructive" });
            return;
          }
          const audioFile = new File([audioBlob], "capture.webm", { type: "audio/webm" });
          toast({ title: "Transcription en cours…" });
          const result = await transcribeAudio.mutateAsync({ data: { audio: audioFile } });
          const transcript = (result?.transcript ?? "").trim();
          if (!transcript) {
            toast({ title: "Transcription vide", variant: "destructive" });
            return;
          }
          setSourceType("voice");
          submitInboxItem("voice", transcript, transcript);
        } catch {
          toast({ title: "Échec de la transcription", variant: "destructive" });
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch {
      toast({ title: "Erreur d'accès au micro", variant: "destructive" });
    }
  };

  const isPending =
    createInbox.isPending || analyzeInbox.isPending || transcribeAudio.isPending;

  const isTranscribing = transcribeAudio.isPending && !isRecording;

  return {
    text,
    setText,
    sourceType,
    setSourceType,
    isRecording,
    isTranscribing,
    isPending,
    submitText,
    toggleRecording,
    projectId,
    projectName,
    setSelectedProject,
  } as const;
}
